import React from "react";
import { createPortal } from "react-dom";
import { Copy, History, Pause, Pencil, Play, Trash2 } from "lucide-react";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";

const MENU_WIDTH = 228;
const MENU_MARGIN = 8;

function menuPosition(position, itemCount) {
  const viewportWidth = typeof document === "undefined"
    ? MENU_WIDTH
    : document.documentElement.getBoundingClientRect().width || document.documentElement.clientWidth || window.innerWidth;
  const viewportHeight = typeof document === "undefined"
    ? itemCount * 38
    : document.documentElement.clientHeight || window.innerHeight;
  return {
    left: Math.max(MENU_MARGIN, Math.min(position.x, viewportWidth - MENU_WIDTH - MENU_MARGIN)),
    top: Math.max(MENU_MARGIN, Math.min(position.y, viewportHeight - itemCount * 38 - MENU_MARGIN)),
  };
}

export default function AutomationTaskContextMenu({
  busy = false,
  onClose,
  onDelete,
  onDuplicate,
  onEdit,
  onExecute,
  onHistory,
  onPause,
  onResume,
  position,
  task,
}) {
  const menuRef = React.useRef(null);
  const actions = React.useMemo(() => {
    if (!task) return [];
    const available = [];
    available.push({ key: "edit", label: "Modifier", icon: Pencil, action: onEdit });
    available.push({ key: "duplicate", label: "Dupliquer", icon: Copy, action: onDuplicate });
    available.push({ key: "history", label: "Historique", icon: History, action: onHistory });
    available.push({ key: "execute", label: "Exécuter", icon: Play, action: onExecute });
    if (task.status === "active") available.push({ key: "pause", label: "Pause", icon: Pause, action: onPause });
    if (task.status === "paused") available.push({ key: "resume", label: "Reprendre", icon: Play, action: onResume });
    available.push({ key: "delete", label: "Supprimer", icon: Trash2, action: onDelete, destructive: true });
    return available;
  }, [onDelete, onDuplicate, onEdit, onExecute, onHistory, onPause, onResume, task]);

  React.useEffect(() => {
    if (!task || !position) return undefined;
    menuRef.current?.querySelector("button:not(:disabled)")?.focus();
    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) onClose?.();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    const handleViewportChange = () => onClose?.();
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [onClose, position, task]);

  if (!task || !position || actions.length === 0) return null;
  const portalRoot = getReactPortalRoot();
  if (!portalRoot) return null;
  const resolvedPosition = menuPosition(position, actions.length);

  const handleMenuKeyDown = (event) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const items = [...event.currentTarget.querySelectorAll("button:not(:disabled)")];
    if (!items.length) return;
    const currentIndex = items.indexOf(document.activeElement);
    const nextIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? items.length - 1
        : event.key === "ArrowDown"
          ? (currentIndex + 1 + items.length) % items.length
          : (currentIndex - 1 + items.length) % items.length;
    items[nextIndex].focus();
  };

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Actions pour la tâche ${task.name}`}
      className="fixed z-[140] w-[228px] rounded-[4px] border border-slate-200 bg-white p-1 shadow-[0_18px_40px_rgba(15,23,42,0.18)]"
      style={{ left: `${resolvedPosition.left}px`, top: `${resolvedPosition.top}px` }}
      onKeyDown={handleMenuKeyDown}
    >
      {actions.map(({ key, label, icon: Icon, action, destructive }) => (
        <button
          key={key}
          type="button"
          role="menuitem"
          disabled={busy}
          className={`flex min-h-9 w-full items-center gap-2 rounded-[4px] px-2.5 py-2 text-left text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-50 ${destructive ? "text-red-700 hover:bg-red-50" : "text-slate-700 hover:bg-slate-50 hover:text-slate-950"}`}
          onClick={() => {
            onClose?.();
            action?.(task);
          }}
        >
          <Icon className="h-3.5 w-3.5 shrink-0" />
          {label}
        </button>
      ))}
    </div>,
    portalRoot,
  );
}
